import type { NoteActivity, NoteActivityVersion, User } from '@paintscout/api'
import { addAuth0Prefix, getActivityColor } from '@paintscout/util'
import type { Theme } from '@material-ui/core/styles'
import { makeStyles } from '@material-ui/core'
import moment from 'moment'
import React from 'react'
import type { StyleClasses } from '@paintscout/ui/styles'
import { Typography, useTranslation } from '@paintscout/ui'
import ViewVersionDialog from '../../dialogs/ViewVersionDialog'
import EstimatorAvatar from '../EstimatorAvatar'
import { useDialogs } from '../../providers/DialogStack'
import { useUsers } from '../../providers/UsersProvider'

const ALIGN_FLEX_START = 'flex-start'

const useStyles = makeStyles<Theme, NoteHistoryPopoverProps>(
  (theme) => ({
    root: {},
    historyPopover: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: ALIGN_FLEX_START,
      alignItems: ALIGN_FLEX_START
    },
    popoverHeader: {
      padding: theme.spacing(1.25),
      paddingBottom: theme.spacing(0.5),
      paddingTop: theme.spacing(1),
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      width: '100%'
    },
    popoverList: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: ALIGN_FLEX_START,
      alignItems: ALIGN_FLEX_START,
      padding: theme.spacing(0.5),
      paddingLeft: 0
    },
    popoverItem: {
      minWidth: '200px',
      width: '100%',
      gap: theme.spacing(1),
      padding: theme.spacing(1),
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderRadius: theme.borderRadius.lg,
      cursor: 'pointer',
      '@media (hover: hover)': {
        '&:hover': {
          backgroundColor: theme.paintscout.palette.brand.Neutral.P94
        }
      }
    },
    itemName: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    dropdownAvatar: {}
  }),
  { name: 'NoteHistoryPopover' }
)

export interface NoteHistoryPopoverProps {
  classes?: StyleClasses<typeof useStyles>
  note: NoteActivity
  totalEdits: number
  onNoteClick: () => void
}

function NoteHistoryPopover(props: NoteHistoryPopoverProps) {
  const classes = useStyles(props)
  const { __ } = useTranslation()
  const { note, totalEdits, onNoteClick } = props
  const { users } = useUsers()
  const { openDialog, dismissDialog } = useDialogs()

  const userMap = Object.values(users).reduce(
    (acc, user) => {
      acc[user.user_id] = user
      return acc
    },
    {} as { [key: string]: User }
  )

  function handleVersionClick(version: NoteActivityVersion) {
    onNoteClick()
    openDialog(ViewVersionDialog, {
      original: note?.details?.versions[totalEdits - 1].timestamp === version.timestamp,
      version,
      user: userMap[addAuth0Prefix(version.owner.id)],
      onCancel: dismissDialog
    })
  }

  return (
    <div className={classes.historyPopover}>
      <div className={classes.popoverHeader}>
        <Typography variant="h6" style={{ textTransform: 'none' }}>
          {__('Version History')}
        </Typography>
      </div>
      <div className={classes.popoverList}>
        {note?.details?.versions?.map((version, i) => {
          const matchingUser = userMap[addAuth0Prefix(version.owner.id)]
          if (!matchingUser) {
            console.warn(`[NoteHistoryPopover] User not found in userMap for owner id: ${version.owner.id}`)
          }
          return (
            <div className={classes.popoverItem} key={i} onClick={() => handleVersionClick(version)}>
              <div className={classes.itemName}>
                <EstimatorAvatar
                  height="20px"
                  width="20px"
                  fontSize="10px"
                  user={version.owner}
                  className={classes.dropdownAvatar}
                  userPictures={{
                    [addAuth0Prefix(version.owner.id)]: matchingUser?.user_metadata?.customAvatar?.cdnValue ?? ''
                  }}
                  avatarColor={getActivityColor(matchingUser?.user_metadata?.color)}
                  style={{
                    paddingTop: 0
                  }}
                  hideTooltip
                />
                <Typography variant="body2" weight={'bold'}>
                  {matchingUser?.name ??
                    (`${version.owner.firstName ?? ''} ${version.owner.lastName ?? ''}`.trim() || __('Unknown User'))}
                </Typography>
              </div>
              <Typography variant="body2">{moment(version.timestamp).fromNow()}</Typography>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default NoteHistoryPopover
